import {getRandomInt} from "../../framework/src/helpers";
import SymbolBlue from "./SymbolBlue";
import SymbolCrown from "./SymbolCrown";
import SymbolGreen from "./SymbolGreen";
import SymbolOrange from "./SymbolOrange";
import SymbolPurple from "./SymbolPurple";
import SymbolRed from "./SymbolRed";
import SymbolSeven from "./SymbolSeven";
import SymbolStar from "./SymbolStar";
import SymbolWild from "./SymbolWild";
import X248 from "./X248";
import WinController from "../win/WinController";
// import MadmaxCommanderBase from "../../framework/src/madmax/MadmaxCommanderBase";


/**
 * Location: (framework)../src/symbols/SymbolAnim.js
 * Location: (game)src/symbols/SymbolAnim.js
 *
 * This class runs the animations. It creates an instance of every Symbol class, initializes them and then calls the
 * {@link Symbol#animate} method.
 * This class also provides static functions for certain operations like cleanup, create mask and start emitter.
 *
 * The animations are composed of 3 parts: start, loop and end.
 * Start and end are always played. Loop is played only when wild appears??? When to play loop and how many times is
 * given by the loop variable passed to {@link SymbolAnim#animate} by {@link WinController}.
 *
 * @property madmax {Madmax}
 * @property commander {Commander}
 * @property layer {PIXI.Container} animations will be added to this layer
 * @property speed {number} multiplier that changes the default speed animation
 * @property BlueAnim {SymbolBlue} reference to class with animation for blue symbol
 * @property CrownAnim {SymbolCrown} reference to class with animation for crown symbol
 * @property GreenAnim {SymbolGreen} reference to class with animation for green symbol
 * @property OrangeAnim {SymbolOrange} reference to class with animation for orange symbol
 * @property PurpleAnim {SymbolPurple} reference to class with animation for purple symbol
 * @property RedAnim {SymbolRed} reference to class with animation for red symbol
 * @property SevenAnim {SymbolSeven} reference to class with animation for seven symbol
 * @property StarAnim {SymbolStar} reference to class with animation for star symbol
 * @property WildAnim {SymbolWild} reference to class with animation for wild symbol
 * @property X248Anim {X248} reference to class with animation for x2 x4 x8 effects
 * @class
 */
class SymbolAnim {
    constructor(commander) {
        this.commander = commander;
        this.layer = this.commander.layers.anims;
        this.winController = this.commander.WinController;

        this.speed = 1;
        this.brightness = {b: 1};

        this.BlueAnim = new SymbolBlue(commander, this.speed);
        this.CrownAnim = new SymbolCrown(commander, this.speed);
        this.GreenAnim = new SymbolGreen(commander, this.speed);
        this.OrangeAnim = new SymbolOrange(commander, this.speed);
        this.PurpleAnim = new SymbolPurple(commander, this.speed);
        this.RedAnim = new SymbolRed(commander, this.speed);
        this.SevenAnim = new SymbolSeven(commander, this.speed);
        this.StarAnim = new SymbolStar(commander, this.speed);
        this.WildAnim = new SymbolWild(commander, this.speed);
        this.X248Anim = new X248(commander, this.speed);

    }

    /**
     * Initialization of the animation parts. It sets the particle texture.
     * (Open for more operations in the future...)
     */
    init() {
        this.BlueAnim.init();
        this.CrownAnim.init();
        this.GreenAnim.init();
        this.OrangeAnim.init();
        this.PurpleAnim.init();
        this.RedAnim.init();
        this.SevenAnim.init();
        this.StarAnim.init();
        this.WildAnim.init();
        this.X248Anim.init();
    }

    /**
     * Call {@link Symbol#animate} method of given symbol to animate it.
     * Multiplier sets how many time
     * @param symbol {PIXI.Sprite} symbol
     * @param long {boolean} true if winline symbol is crown star or seven
     * @param multiplier {number} line win multiplier
     * @return {Promise<any>}
     */
    animateSymbol(symbol, multiplier, long) {
        return new Promise(res => {
            this.darkenReels();
            let loop = 0;
            if (multiplier > 1) {
                loop += 2;
                if (long)
                    loop += 3;
                this.X248Anim.animateX(symbol, multiplier, long);
            }

            switch (symbol.name) {
                case "symbol_Blue":
                    this.BlueAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Crown":
                    this.CrownAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Green":
                    this.GreenAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Orange":
                    this.OrangeAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Purple":
                    this.PurpleAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Red":
                    this.RedAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Seven":
                    this.SevenAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Star":
                    this.StarAnim.animate(symbol,loop).then(() => res());
                    break;
                case "symbol_Wild":
                    if (long)
                        this.WildAnim.animateLong(symbol).then(() => res());
                    else
                        this.WildAnim.animateShort(symbol).then(() => res());
                    break;
            }
        });
    }

    /**
     * Create a sprite, set its position, anchor and texture and return it.
     * @param x {number}
     * @param y {number}
     * @param name {string} symbol name
     * @return {Sprite}
     */
    static createMask(x, y, name) {
        let mask = new PIXI.Sprite(PIXI.Texture.from(name+"_alpha.png"));
        mask.position.set(x,y);
        mask.anchor.set(0.5, 0.5);
        return mask;
    }

    /**
     * Run the emitter.
     * See configs at file {@link Symbol} at the bottom.
     * @param emitter {PIXI.particles.Emitter}
     */
    static runEmitter(emitter) {
        emitter.emit = true;
        emitter.autoUpdate = true;
    }

    /**
     * Remove children from layer.
     */
    cleanup() {
        this.layer.removeChildren();
    }

    /**
     * Remove Tween from sprite. Used to cleanup after every tween and animation.
     * @param sprite
     */
    static removeTween(sprite) {
        createjs.Tween.removeTweens(sprite);
    }

    /**
     * Remove Sprite. !!!THROWS ERROR THAT PARENT IS UNDEFINED!!!
     * TO FIX!
     * @param sprite
     */
    static removeSprite(sprite, parent) {
        parent.removeChild(sprite);
    }

    /**
     * Create sprite on given position with given texture and set its anchor.
     * If anchor not defined, default value is 0.5,0.5.
     * @param x
     * @param y
     * @param name
     * @param anchor
     * @return {Sprite}
     */
    static createSprite(x, y, name, anchor = { x: 0.5, y: 0.5 }) {
        let sprite = new PIXI.Sprite(PIXI.Texture.from(name));
        sprite.position.set(x,y);
        sprite.anchor.set(anchor.x, anchor.y);
        return sprite;
    }

    /**
     * Return random point [x,y] with positions similar to given x and y but within ranges <x-50,x+50> and <y-50,y+50>
     * !!NOT USED!!
     * @param x
     * @param y
     * @return {number[]}
     */
    static getRandomPoint(x,y) {
        return [getRandomInt(x-50,x+50),getRandomInt(y-50,y+50)];
    }

    /**
     * Create a tween that changes the brightness to 0.5 of reels layer by calling {@link MadmaxCommander#reelsBrightness}.
     */
    darkenReels() {
        createjs.Tween.get(this.brightness)
            .to({b: 0.5}, 200, createjs.Ease.linear)
            .call(() => {SymbolAnim.removeTween(this.brightness)})
            .addEventListener("change", () => {
                this.commander.reelsBrightness(this.brightness.b);
            });
    }

    /**
     * Create a tween that changes the brightness to 1.0 of reels layer by calling {@link MadmaxCommander#reelsBrightness}.
     */
   lightenReels() {
        createjs.Tween.get(this.brightness)
            .to({b: 1}, 200, createjs.Ease.linear)
            .call(() => {
                SymbolAnim.removeTween(this.brightness);
                this.commander.WinController.removeWinSymbols();
            })
            .addEventListener("change", () => {
                this.commander.reelsBrightness(this.brightness.b);
            });
    }
}
export default SymbolAnim;